import { useAuthContext } from 'contexts';
import type { Annotation } from 'fhir/r4';
import moment from 'moment';
import React, { useMemo } from 'react';
import {
  Button,
  Comment,
  CommentAction,
  CommentActions,
  CommentAuthor,
  CommentAvatar,
  CommentContent,
  CommentGroup,
  CommentMetadata,
  CommentText,
  Header,
  Message,
} from 'semantic-ui-react';
import { getImage } from 'utils';

type GroupNoteProps = {
  handleSelectedNote: (note: string | null) => void;
  isNew?: boolean | undefined;
  isSubmitting: boolean;
  noteList: Annotation[];
  removeNote?: (note: string) => Promise<void>;
  selectedNote: string | null;
};

const GroupNote: React.FC<GroupNoteProps> = ({ handleSelectedNote, isNew, noteList, removeNote, selectedNote }) => {
  const { gender } = useAuthContext();

  const sortedNoteList = useMemo(() => {
    return noteList
      .slice() // Create a shallow copy to avoid mutating the original array
      .sort((a, b) => new Date(b.time!).getTime() - new Date(a.time!).getTime());
  }, [noteList]);

  const isMale = gender === 'male';

  return (
    <>
      <Header as="h4" dividing>
        Notes History
      </Header>
      <CommentGroup style={{ height: isNew ? '50vh' : 'auto', overflowY: isNew ? 'auto' : 'hidden' }}>
        {sortedNoteList.length > 0 &&
          sortedNoteList.map((note, index) => {
            const noteTime = moment(note.time).format('MM-DD-YYYY');
            const authorName = note.authorString;
            return (
              <Comment key={index}>
                <CommentAvatar src={`/images/${getImage(isMale, authorName)}`} />
                <CommentContent>
                  <CommentAuthor as="a">{authorName}</CommentAuthor>
                  <CommentMetadata>
                    <div>{noteTime}</div>
                  </CommentMetadata>
                  <CommentText>{note.text}</CommentText>
                  <CommentActions>
                    <CommentAction>
                      {removeNote && <Button
                        compact
                        size="mini"
                        color="red"
                        content="Remove"
                        onClick={() => handleSelectedNote(index.toString())}
                      />}
                    </CommentAction>
                  </CommentActions>
                  {selectedNote === index.toString() && (
                    <>
                      <Message warning>Are you sure?</Message>
                      <Button.Group>
                        <Button
                          compact
                          size="mini"
                          color="grey"
                          content="Cancel"
                          onClick={() => handleSelectedNote(null)}
                        />
                        <Button
                          compact
                          size="mini"
                          color="blue"
                          content="OK!"
                          onClick={() => removeNote!(selectedNote)}
                        />
                      </Button.Group>
                    </>
                  )}
                </CommentContent>
              </Comment>
            );
          })}
        {sortedNoteList.length === 0 && <Message info>No notes available.</Message>}
      </CommentGroup>
    </>
  );
};

export default GroupNote;
